import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import {
  useVisibleGoals,
  useVisibleInitiatives,
  useVisiblePriorities,
  useVisibleTasks,
  useAffiliatedHolders,
  useVisibleContacts,
} from '../hooks/useScopedData';
import { taskIsOverdue, taskIsDueSoon } from '../lib/workItems';
import Spinner from '../components/Spinner';
import HealthSections from './dashboard/HealthSections';
import { isAdmin, hasCouncilDashboardAccess } from '../lib/visibility';
import { buildOrgForest, countSeats } from '../lib/orgChart';
import { recName, statusIsComplete, type AppSettings, type Goal, type Task } from '../types';

const ATTENTION_CAP = 10;

export default function Dashboard() {
  const { profile, settings, districtFilter } = useAppContext();
  const navigate = useNavigate();
  const priorities = useVisiblePriorities();
  const goalsQ = useVisibleGoals(profile);
  const tasksQ = useVisibleTasks(profile);
  const contactsQ = useVisibleContacts(profile);
  const holdersQ = useAffiliatedHolders(profile);
  // Initiative counts are a nice-to-have on the Strategic Plan strip, not a
  // primary signal — this collection is small and manually curated (unlike
  // Actions, which Phase 2/3 automation now keeps growing), so reading it
  // here is cheap and doesn't need the same caution.
  const initiativesQ = useVisibleInitiatives(profile);

  const done = (t: Task) => statusIsComplete(settings.taskStatuses, t.status);

  const tasks = useMemo(
    () =>
      districtFilter === 'All'
        ? tasksQ.data
        : tasksQ.data.filter((t) => t.districtId === districtFilter),
    [tasksQ.data, districtFilter],
  );

  const overdue = tasks.filter((t) => taskIsOverdue(t, done(t)));
  const soon = tasks.filter((t) => taskIsDueSoon(t, done(t)));

  const goalsAtRisk = useMemo(
    () =>
      goalsQ.data.filter(
        (g) =>
          g.status === 'active' &&
          tasks.some((t) => t.goalId === g.id && taskIsOverdue(t, done(t))),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goalsQ.data, tasks, settings.taskStatuses],
  );

  const attention = useMemo(
    () => [
      ...goalsAtRisk.map((goal) => ({ kind: 'goal' as const, goal })),
      ...overdue.map((task) => ({ kind: 'task' as const, task })),
      ...soon.map((task) => ({ kind: 'task' as const, task })),
    ].slice(0, ATTENTION_CAP),
    [goalsAtRisk, overdue, soon],
  );

  // Per-Strategic-Area rollup for the Council Strategic Plan strip. Goals
  // are council-level strategy (every signed-in user sees every goal,
  // regardless of district — see useVisibleGoals), so goal/initiative
  // counts here are always council-wide, not re-filtered by the district
  // dropdown; only the "needs attention" signal inherits whatever
  // district scoping `goalsAtRisk` already has (a non-admin only ever
  // sees their own district's tasks, so their attention count reflects
  // that — everyone else's KPI tiles behave the same way).
  //
  // Action counts were deliberately left out: in the current data, all but
  // one of 176+ Actions are Phase 2/3 system-generated (SYT/Advancement)
  // and relate to a Unit or Contact, never a Goal — so a
  // Goal-linked-Action count would show ~0 everywhere today, and computing
  // it means reading the whole (automation-fed, ever-growing) actions
  // collection just to find that. Not worth the read for a number that
  // isn't meaningful yet.
  const strategicAreaStats = useMemo(() => {
    const stats = new Map<string, { goals: number; initiatives: number; atRisk: number }>();
    for (const p of priorities.data) stats.set(p.id, { goals: 0, initiatives: 0, atRisk: 0 });
    for (const g of goalsQ.data) {
      const s = stats.get(g.strategicPriorityId);
      if (s) s.goals++;
    }
    const goalById = new Map(goalsQ.data.map((g) => [g.id, g]));
    for (const init of initiativesQ.data) {
      if (!init.strategicGoalId) continue;
      const goal = goalById.get(init.strategicGoalId);
      if (!goal) continue;
      const s = stats.get(goal.strategicPriorityId);
      if (s) s.initiatives++;
    }
    for (const g of goalsAtRisk) {
      const s = stats.get(g.strategicPriorityId);
      if (s) s.atRisk++;
    }
    return stats;
  }, [priorities.data, goalsQ.data, initiativesQ.data, goalsAtRisk]);

  const activeAreas = useMemo(
    () => priorities.data.filter((p) => p.status === 'active'),
    [priorities.data],
  );

  const openStrategicArea = (id: string) => navigate('/strategy', { state: { focusPriorityId: id } });

  // A non-admin's districtFilter is always 'All' (there's no picker to move
  // it — see Layout.tsx below), even though useVisibleContacts/etc. already
  // scope their data to just their own district — so "All" only means a
  // genuine council-wide view for admin/council-wide viewers. Dashboard
  // Only is intentionally included here (hasCouncilDashboardAccess covers
  // dashboardOnly/fullRead/fullRW) — seeing the council-wide safe-aggregate
  // rollup on the Dashboard itself is the entire point of that tier; only
  // drill-down INTO other modules is restricted separately — see
  // HealthSections.tsx's own canDrillIntoModules gating.
  const wide = isAdmin(profile) || hasCouncilDashboardAccess(profile);

  // "Roster Filled" = how many of the district's actual committee SEATS
  // (every Position in settings.positions, per Org Chart's own reporting
  // forest) have a real, non-vacant holder — NOT how many Contact records
  // happen to already exist. A position nobody has ever been added for is
  // just as vacant as one holding a literal "Vacant" placeholder Contact,
  // and counting only existing docs made a barely-staffed district look
  // artificially "100% filled" (e.g. 2 Contact docs on file, both named,
  // read as "2/2" even though the district's real ~18-seat roster was
  // mostly empty). Reuses the exact same countSeats() Org Chart's own
  // "Seats filled" figure is built from, so the two screens can never
  // disagree with each other.
  // Council Organization Phase B: council-level Positions are never part of
  // a district's roster — filtered out before building the forest, exactly
  // like the Org Chart page's own district forest (src/views/OrgChart.tsx),
  // so a council vacancy never inflates a district's seat denominator.
  const districtPositions = useMemo(
    () => settings.positions.filter((p) => (p.organizationLevel ?? 'district') === 'district'),
    [settings.positions],
  );
  const forest = useMemo(() => buildOrgForest(districtPositions), [districtPositions]);
  const roster = useMemo(() => {
    if (wide && districtFilter === 'All') {
      return settings.districts.reduce(
        (acc, d) => {
          const s = countSeats(forest, contactsQ.data, d.id, holdersQ.holders);
          return { filled: acc.filled + s.filled, seats: acc.seats + s.seats };
        },
        { filled: 0, seats: 0 },
      );
    }
    const targetDistrict = wide ? districtFilter : (profile.districtId ?? districtFilter);
    return countSeats(forest, contactsQ.data, targetDistrict, holdersQ.holders);
  }, [wide, forest, contactsQ.data, holdersQ.holders, districtFilter, settings.districts, profile.districtId]);

  const taskMetricsLoading = tasksQ.loading || goalsQ.loading;
  const metrics = [
    { label: 'Overdue Tasks', value: overdue.length, loading: taskMetricsLoading },
    { label: 'Due in 7 Days', value: soon.length, loading: taskMetricsLoading },
    { label: 'Goals At Risk', value: goalsAtRisk.length, loading: taskMetricsLoading },
    {
      label: 'Roster Filled',
      value: `${roster.filled} / ${roster.seats || 0}`,
      loading: contactsQ.loading || holdersQ.loading,
    },
  ];

  // Label by their real district rather than the unused filter state, so
  // this doesn't misleadingly say "across all districts" for someone who
  // only ever sees one.
  const scopeLabel = wide
    ? districtFilter === 'All'
      ? 'Across all districts'
      : `${recName(settings.districts, districtFilter)} district`
    : `${recName(settings.districts, profile.districtId ?? '')} district`;

  return (
    <>
      <div className="card">
        <div className="sp-strip-head">
          <div>
            <b>Council Strategic Plan</b>
            <div className="subtitle">Strategy → Goals → Action</div>
          </div>
          {activeAreas.length > 0 && (
            <span className="subtitle">
              {activeAreas.length} Strategic Area{activeAreas.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {priorities.loading && priorities.data.length === 0 && (
          <Spinner label="Loading Strategic Areas…" />
        )}
        {!priorities.loading && activeAreas.length === 0 && (
          <div className="sp-empty">No Strategic Areas yet — see Strategic Plan.</div>
        )}
        {activeAreas.length > 0 && (
          <div className="sp-strip-areas">
            {activeAreas.slice(0, 8).map((p) => {
              const s = strategicAreaStats.get(p.id) ?? { goals: 0, initiatives: 0, atRisk: 0 };
              return (
                <button
                  key={p.id}
                  type="button"
                  className="sp-area"
                  onClick={() => openStrategicArea(p.id)}
                  title={`Open ${p.title} in the Strategic Plan`}
                >
                  <div className="sp-area-name">{p.title}</div>
                  <div className="sp-area-counts">
                    {s.goals} Goal{s.goals === 1 ? '' : 's'}
                    {s.initiatives > 0 && (
                      <> · {s.initiatives} Initiative{s.initiatives === 1 ? '' : 's'}</>
                    )}
                  </div>
                  <div className={`sp-area-attn ${s.atRisk > 0 ? 'is-attn' : 'is-ok'}`}>
                    {s.atRisk > 0 ? `${s.atRisk} need${s.atRisk === 1 ? 's' : ''} attention` : 'On track'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="metrics">
        {metrics.map((m) => (
          <div className="metric" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.loading ? <Spinner size={20} /> : m.value}</strong>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="row">
          <div>
            <h2>Needs attention</h2>
            <div className="subtitle">{scopeLabel}</div>
          </div>
          <Link className="btn" to="/goals">
            Open Goals &amp; Tasks
          </Link>
        </div>

        {(tasksQ.loading || goalsQ.loading) && (
          <div style={{ padding: '10px 0' }}>
            <Spinner label="Loading…" />
          </div>
        )}
        {!tasksQ.loading && !goalsQ.loading && attention.length === 0 && (
          <div className="empty">Nothing at risk, overdue, or due this week. 🎉</div>
        )}

        {attention.map((item) =>
          item.kind === 'goal' ? (
            <GoalLine key={`g:${item.goal.id}`} goal={item.goal} settings={settings} />
          ) : (
            <TaskLine key={`t:${item.task.id}`} task={item.task} settings={settings} />
          ),
        )}
      </div>

      <HealthSections />
    </>
  );
}

function TaskLine({ task, settings }: { task: Task; settings: AppSettings }) {
  const complete = statusIsComplete(settings.taskStatuses, task.status);
  const od = taskIsOverdue(task, complete);
  return (
    <div className="task">
      <div>
        <b>{task.title}</b>
        <br />
        <small>{recName(settings.committees, task.committee)}</small>
      </div>
      <div>{recName(settings.districts, task.districtId)}</div>
      <div>
        {task.assignedPosition
          ? `◉ ${task.assignedPosition}`
          : task.assignedContactIds?.length || task.assignedPersonIds?.length
            ? '● direct'
            : '—'}
      </div>
      <div>
        {task.dueDate}
        <br />
        <span className={`badge ${od ? 'warn' : 'neutral'}`}>{od ? 'Overdue' : 'Due soon'}</span>
      </div>
    </div>
  );
}

function GoalLine({ goal, settings }: { goal: Goal; settings: AppSettings }) {
  const districts = (goal.districtIds ?? [])
    .map((id) => recName(settings.districts, id))
    .join(', ');
  return (
    <div className="task">
      <div>
        <b>{goal.title}</b>
        <br />
        <small>{recName(settings.committees, goal.committee)}</small>
      </div>
      <div>{districts || 'All districts'}</div>
      <div>Goal</div>
      <div>
        <span className="badge warn">At risk</span>
      </div>
    </div>
  );
}
